import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { GuestGuideView } from "@/components/GuestGuideView";
import { ConciergeProvider } from "@/components/ConciergeChat";
import { themeTokens } from "@/lib/sections";
import { API } from "@/lib/api";
import { Loader2, ChevronDown } from "lucide-react";
import { LogoSymbol } from "@/components/Brand";
import { LANG_NATIVE, LANG_FLAG, LANG_SHORT, GUEST_LANGS } from "@/lib/languages";
import { useSeo } from "@/lib/seo";

export default function GuestGuide() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [error, setError] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [lang, setLang] = useState("it");
  const [menu, setMenu] = useState(false);
  useSeo({ noindex: true });

  useEffect(() => {
    axios.get(`${API}/public/guide/${slug}`)
      .then(({ data }) => {
        if (data.unavailable) { setUnavailable(true); return; }
        setGuide(data.guide); setLang(data.guide.base_language || "it");
      })
      .catch(() => setError(true));
  }, [slug]);

  if (unavailable) return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center px-6 text-center" data-testid="guide-unavailable">
      <LogoSymbol className="h-12" />
      <h1 className="mt-5 font-serif-display text-2xl text-slate-800">Guida non disponibile</h1>
      <p className="mt-2 text-slate-500 max-w-xs">Questa guida non è al momento disponibile. Ti invitiamo a contattare direttamente il tuo host.</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center px-6 text-center">
      <LogoSymbol className="h-12" />
      <h1 className="mt-5 font-serif-display text-2xl text-slate-800">Guida non trovata</h1>
      <p className="mt-2 text-slate-500">Questa guida non esiste o non è ancora stata pubblicata.</p>
    </div>
  );
  if (!guide) return <div className="min-h-screen bg-slate-100 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  const languages = guide.languages || ["it"];
  const T = themeTokens(guide.branding?.theme, guide.branding?.brand_color);

  return (
    <div className="min-h-screen bg-slate-100 flex justify-center md:py-8">
      <div className="guest-theme w-full max-w-md relative overflow-hidden min-h-screen md:min-h-[86vh] md:rounded-[2rem] md:shadow-2xl md:shadow-slate-300/60" style={{ background: T.bg }}>
        {languages.length > 1 && (
          <div className="absolute top-4 right-4 z-30">
            <button onClick={() => setMenu((m) => !m)} data-testid="guest-lang-toggle" className="flex items-center gap-1.5 bg-white/90 backdrop-blur rounded-full px-3 py-1.5 text-sm font-semibold text-slate-700 shadow"><span>{LANG_FLAG[lang]}</span> {LANG_SHORT[lang]} <ChevronDown className="w-3.5 h-3.5" /></button>
            {menu && <div className="absolute right-0 mt-1 bg-white rounded-xl shadow-lg border border-slate-100 py-1 w-40" onMouseLeave={() => setMenu(false)}>{GUEST_LANGS.filter((l) => languages.includes(l.code)).map((l) => <button key={l.code} onClick={() => { setLang(l.code); setMenu(false); }} data-testid={`guest-lang-${l.code}`} className={`flex items-center gap-2 w-full text-left px-3 py-2 text-sm ${lang === l.code ? "text-blue-600 font-semibold" : "text-slate-600"}`}><span>{l.flag}</span> {LANG_NATIVE[l.code]}</button>)}</div>}
          </div>
        )}
        <ConciergeProvider enabled={guide.ai_concierge?.enabled !== false} mode="public" cid={slug} brand={T.accent} T={T} lang={lang}>
          <GuestGuideView guide={guide} lang={lang} bottomNav />
        </ConciergeProvider>
      </div>
    </div>
  );
}
